// Frittstående Feltveksler (poppet ut til egen skjerm — TOC-samband på skjerm 2).
// Egen WS-feed (cookie-auth); kanal-chat + GNÅ 1:1 via hubbens collab-fanout.
//
// EIERKRAV 2026-08-13: «Feltveksleren må være lik — selv om du velger å "poppe
// den ut"». Den var det IKKE. Tre konkrete avvik, som alle er lukket her:
//
//  1. INGEN GNÅ-KATALOG. `sokGnaBrukere` ble ikke sendt inn, så søket her
//     filtrerte bare enhetskontoene og det som lå i localStorage. Søk på «dan»
//     traff derfor stillingskontoer som `skade_hv-11` og ga inntrykk av at
//     ingen registrert GNÅ-bruker fantes. Hovedvinduet slo opp i hubbens
//     katalog over alle registrerte brukere; dette vinduet gjorde det ikke.
//  2. INGEN LÆRTE KONTAKTER. `getSeen: () => []` var hardkodet, så en som
//     svarte i pop-outen forsvant fra lista ved neste gjentegning.
//  3. INGEN LAGRING. Kontakter man la til her ble aldri skrevet tilbake, mens
//     hovedvinduet persisterte samme nøkkel. To vinduer, to sannheter.
//
// Varslingen (lyd + teller i vindustittel + systemvarsel) ligger i selve
// `ui/sentralbord.ts` og gjelder derfor automatisk her — det var HELE grunnen
// til å legge den i panelet og ikke i hovedvinduets skall.
import { parseChatEvent, type Contact } from '@skade/contracts';
import type { Feed } from '../data/feed.js';
import type { SyncBus } from '../sync.js';
import type { User } from '@skade/rbac';
import { Sentralbord } from '../ui/sentralbord.js';
import { sokGnaBrukere } from '../gna-katalog.js';

/** Samme nøkkel som hovedvinduet (`main.ts`: CONTACTS_KEY) — samme kontaktbok. */
function kontaktnokkel(user: User): string {
  return `skade.contacts.${user.org?.district ?? 'na'}`;
}

function loadContacts(user: User): Map<string, Contact> {
  const ut = new Map<string, Contact>();
  try {
    const raw = JSON.parse(localStorage.getItem(kontaktnokkel(user)) ?? '[]');
    if (Array.isArray(raw)) {
      for (const c of raw) if (c?.id) ut.set(c.id, { id: c.id, name: c.name || c.id, source: 'saved' });
    }
  } catch { /* korrupt cache */ }
  return ut;
}

export function mountSentralbordPanel(root: HTMLElement, feed: Feed, _sync: SyncBus, user: User): void {
  document.body.classList.add('panel-standalone');
  // Eget vindu → egen tittel. Uten dette het vinduet «SKADE — Situasjonsbilde»
  // som hovedvinduet, og uleste-telleren («(3) …») sto på en tittel operatøren
  // ikke kunne skille fra kartvinduet i Dock/oppgavelinja.
  try { document.title = 'Feltveksler · SKADE'; } catch { /* ok */ }
  root.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = 'standalone-panel';
  const head = document.createElement('div');
  head.className = 'standalone-head';
  head.append(span('brand', 'SKADE'), span('standalone-title', 'Feltveksler'));
  wrap.append(head);

  const saved = loadContacts(user);
  const seen = new Map<string, Contact>();
  function persist() {
    try { localStorage.setItem(kontaktnokkel(user), JSON.stringify([...saved.values()])); } catch { /* full */ }
  }
  /** Lær en avsender vi ikke har fra før, slik hovedvinduet gjør. */
  function learn(id: string, name?: string) {
    if (!id || saved.has(id) || seen.has(id)) return;
    seen.set(id, { id, name: name || id, source: 'seen' });
  }

  const sb = new Sentralbord({
    selfId: user.id,
    embedded: true,
    onChat: (e) => feed.send?.({ type: 'chat', chat: e }),
    getSaved: () => [...saved.values()],
    getSeen: () => [...seen.values()],
    // Samme tilgivende adressering som i hovedvinduet: skriver operatøren «@dan»
    // i søkefeltet, skal domenet fylles ut her også.
    defaultDomain: user.id.includes(':') ? user.id.split(':').slice(1).join(':') : undefined,
    // Katalogen over ALLE registrerte GNÅ-brukere (hubben holder admin-tokenet).
    // Dette manglet — se punkt 1 i toppkommentaren.
    sokGnaBrukere,
  });
  wrap.append(sb.el);
  root.append(wrap);
  // Kontakter lagt til her skal overleve, som i hovedvinduet.
  persist();

  feed.onCollab?.((m) => {
    if (m.kind === 'chat' && m.chat) {
      const e = parseChatEvent(m.chat);
      if (e) { learn(e.author); sb.ingestChat(e); }
    } else if (m.kind === 'gnadm' && m.dm) {
      const dm = m.dm as { from?: string; text?: string; tsMs?: number };
      if (dm.from) { learn(dm.from); sb.ingestInbound(dm.from, dm.text ?? '', dm.tsMs ?? Date.now()); }
    }
  });
  feed.start();
}

function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
